import {JSX} from "react";

import Image from "next/image";
import type {ImageProps} from "next/image";

// Function to extract URL from different src types
function getImageSrc(src: ImageProps["src"]): string {
    if (typeof src === "string") {
        return src;
    }
    // StaticImageData has src property
    if (typeof src === "object" && "src" in src) {
        return src.src;
    }
    // StaticRequire has default property
    if (typeof src === "object" && "default" in src) {
        return typeof src.default === "string" ? src.default : src.default.src;
    }
    return "";
}

// Check if it's an SVG file
function isSvg(src: ImageProps["src"]): boolean {
    const srcUrl = getImageSrc(src);
    return srcUrl.toLowerCase().endsWith(".svg");
}

// Normalize protocol-relative URLs to absolute HTTPS URLs
// Converts //domain.com/image.jpg → https://domain.com/image.jpg
function normalizeProtocolRelativeUrl(src: ImageProps["src"]): ImageProps["src"] {
    if (typeof src === "string" && src.startsWith("//")) {
        return `https:${src}`;
    }
    return src;
}

// Check if image is from Imgix CDN (already optimized)
function isImgix(src: ImageProps["src"]): boolean {
    const srcUrl = getImageSrc(src);
    return srcUrl.includes("imgix.net") || srcUrl.includes("images.casago.com");
}

// Build Imgix URL with optimization parameters
function buildImgixUrl(
    src: string,
    width?: number | string,
    height?: number | string,
    quality?: number,
    fit?: "crop" | "clip" | "fill" | "scale" | "max" | false,
): string {
    try {
        const url = new URL(src);

        if (width) url.searchParams.set("w", width.toString());
        if (height) url.searchParams.set("h", height.toString());
        if (fit !== false) url.searchParams.set("fit", fit ?? "crop");

        url.searchParams.set("q", (quality ?? 80).toString());
        url.searchParams.set("auto", "format");

        return url.toString();
    } catch {
        return src;
    }
}

export default function SmartImage({
    noImgixCrop,
    ...props
}: Omit<ImageProps, "unoptimized"> & Required<Pick<ImageProps, "alt">> & {noImgixCrop?: boolean}): JSX.Element {
    const isSvgImage = isSvg(props.src);
    const isImgixImage = isImgix(props.src);
    let processedSrc = normalizeProtocolRelativeUrl(props.src);

    if (isImgixImage && typeof processedSrc === "string") {
        const width = typeof props.width === "number" ? props.width : undefined;
        const height = typeof props.height === "number" ? props.height : undefined;
        const quality = typeof props.quality === "number" ? props.quality : undefined;
        const fit = noImgixCrop ? "max" : "crop";

        processedSrc = buildImgixUrl(processedSrc, width, height, quality, fit);
    }

    const shouldSkipOptimization = isSvgImage || isImgixImage;

    return <Image {...props} src={processedSrc} alt={props.alt} unoptimized={shouldSkipOptimization} />;
}
